import {
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';

import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { t } from 'i18next';

import {
  ADMIN_ROLE,
  GENERAL_ADMIN_ROLE,
  STATUS_PENDING,
} from '../assets/AppConstants';

import API from '../interceptors/Api';
import APIExport from '../interceptors/ApiExport';

import { useAppContext } from '../contexts/app';
import { useUserContext } from '../contexts/user';
import {
  getUserWorkspacesByClient,
} from '../services/UserWorkspaces';

import { updateWorkspace } from '../services/Workspaces';
import { getHomeFromRole } from '../utils/routes';
import { getAllTasks, getTasks } from '../services/Tasks';
import { sessionsCheck } from '../services/Sessions';
import { getAllLinks } from '../services/Links';
import { useDebouncedDeepCompareEffect } from './useDebouncedEffect';

const emptyWorkspace = {
  currentUser: {
    role: null,
  },
  id: null,
};

export const useDefaultWorkspace = () => {
  const location = useLocation();
  const { workspace } = useParams();
  const {
    handleAlert,
    blockScreen,
  } = useAppContext();
  const {
    userSession,
    getUserRole,
    getUserStatus,
    userIsLogged,
    handleChangeClientId,
  } = useUserContext();

  const [searchParams] = useSearchParams();
  const navigateRedirect = useNavigate();

  const [reload, setReload] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [openEmptyWorkspace, setOpenEmptyWorkspace] = useState(false);
  const [defaultWorkspace, setDefaultWorkspace] = useState(emptyWorkspace);

  const [isOnboarding, setIsOnboarding] = useState(false);

  const reloadClient = useRef(false);

  const getCallBackOrDefaulUrl = ({ workspaceIndex, role }) => {
    const callbackUrl = searchParams.get('callbackUrl')
      || `/${workspaceIndex}${getHomeFromRole(role)}`
      || '/';

    return callbackUrl;
  };

  const navigateDefaultClientOrFirst = (allWorkspaces) => {
    const defaultClientIndex = allWorkspaces.findIndex(({ currentUser }) => currentUser.default);
    const index = defaultClientIndex !== -1 ? defaultClientIndex : 0;
    const actualWs = allWorkspaces[index];

    navigateRedirect(
      getCallBackOrDefaulUrl({ workspaceIndex: index, role: actualWs.currentUser.role }),
      {
        replace: true,
        state: {
          fromHome: true,
        },
      },
    );
  };

  const navigateOnboarding = (index) => {
    const url = `/${index}/onboarding`;

    navigateRedirect(
      url,
      {
        state: {
          fromHome: true,
        },
      },
    );
  };

  useEffect(() => {
    const fn = async () => {
      try {
        await sessionsCheck();
        setIsAllowed(true);
      } catch (error) {
        handleAlert(error, 'error');
        setIsAllowed(false);
      }
    };

    if (userIsLogged() && getUserStatus() !== STATUS_PENDING) {
      fn();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useDebouncedDeepCompareEffect(() => {
    const loadWorkspaces = async () => {
      try {
        const [workspaceList] = await Promise.all([
          getUserWorkspacesByClient(),
        ]);

        const newWorkspaces = workspaceList.filter(({ currentUser }) => currentUser.status === 'ACTIVE');

        if (reloadClient.current) {
          navigateDefaultClientOrFirst(newWorkspaces);
          reloadClient.current = false;
          window.location.reload();
        }

        setWorkspaces(newWorkspaces.map((wk) => ({ ...wk, typeName: t(wk.parentId ? 'View' : 'Workspace') })));
        setOpenEmptyWorkspace(newWorkspaces.length === 0);

        if (newWorkspaces.length > 0) {
          const index = Number(workspace);

          const isWrongWorkspace = (
            index === undefined || index === null
            || Number.isNaN(index) || index > newWorkspaces.length - 1
          );

          if (isWrongWorkspace) {
            navigateDefaultClientOrFirst(newWorkspaces);
          } else if (index < newWorkspaces.length) {
            const actualWs = newWorkspaces[index];

            if (actualWs.parentId) {
              API.defaults.headers.common['X-Workspace-Id'] = actualWs.parentId;
              APIExport.defaults.headers.common['X-Workspace-Id'] = actualWs.parentId;

              API.defaults.headers.common['X-Workspace-View-Id'] = actualWs.id;
              APIExport.defaults.headers.common['X-Workspace-View-Id'] = actualWs.id;
            } else {
              API.defaults.headers.common['X-Workspace-Id'] = actualWs.id;
              APIExport.defaults.headers.common['X-Workspace-Id'] = actualWs.id;

              API.defaults.headers.common['X-Workspace-View-Id'] = null;
              APIExport.defaults.headers.common['X-Workspace-View-Id'] = null;
            }

            const [allEnabledTasks, links, allTasksRes] = await Promise.all([
              getTasks(),
              getAllLinks(),
              getAllTasks(),
            ]);

            setTasks(allEnabledTasks);
            setAllTasks(allTasksRes);

            const newIsOnboarding = links.content.length === 0
              && getUserRole() === GENERAL_ADMIN_ROLE;

            setIsOnboarding(() => newIsOnboarding);

            if (newIsOnboarding) navigateOnboarding(index);

            setTimeout(() => {
              setDefaultWorkspace(actualWs);
            }, [500]);
          }
        }
      } catch (error) {
        handleAlert(error, 'error');
      }
    };

    if (isAllowed) {
      if (userIsLogged() && getUserStatus() !== STATUS_PENDING) {
        loadWorkspaces();
      } else {
        setDefaultWorkspace(emptyWorkspace);
      }
    }
  }, [reload, userSession, workspace, isAllowed]);

  const handleChangeWsAndClient = (data) => {
    handleChangeClientId(data);
    reloadClient.current = true;
  };

  const handleEditWorkspace = async (data) => {
    blockScreen(true);
    try {
      await updateWorkspace(defaultWorkspace.id, { ...data, id: defaultWorkspace.id });
      handleAlert('Workspace successfully saved!', 'success');
    } catch (error) {
      handleAlert(error, 'error');
    } finally {
      blockScreen(false);
    }
  };

  const getWorkspaceRole = () => defaultWorkspace.currentUser.role;
  const isWorkspaceAdmin = () => getWorkspaceRole() === ADMIN_ROLE;

  const redirectHome = () => {
    navigateRedirect(
      getCallBackOrDefaulUrl({ workspaceIndex: workspace, role: getWorkspaceRole() }),
    );
  };

  const handleNavigateByIndex = (index) => {
    const actualWs = workspaces[index];
    navigateRedirect(
      getCallBackOrDefaulUrl({ workspaceIndex: index, role: actualWs.currentUser.role }),
    );

    window.location.reload();
  };

  const reloadWorkspaces = () => setReload(!reload);
  const handleCloseEmptyWorkspace = () => setOpenEmptyWorkspace(false);

  const value = useMemo(() => ({
    tasks,
    allTasks,
    workspaces,
    isOnboarding,
    defaultWorkspace,
    openEmptyWorkspace,
    redirectHome,
    setIsOnboarding,
    getWorkspaceRole,
    isWorkspaceAdmin,
    reloadWorkspaces,
    handleEditWorkspace,
    handleNavigateByIndex,
    handleChangeWsAndClient,
    handleCloseEmptyWorkspace,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [defaultWorkspace, workspaces, openEmptyWorkspace, tasks, allTasks, isOnboarding]);

  return value;
};
